import {
  HubConnectionBuilder,
  HubConnection,
  HubConnectionState,
  HttpTransportType,
  LogLevel,
} from '@microsoft/signalr'
import { isEntraConfigured } from '../auth'
import { getApiKey, getBearerToken } from './authHeaders'

// In production, connect directly to the API App Service to bypass SWA linked backend
// (SWA doesn't proxy POST/WebSocket to non-/api paths). Falls back to relative path for local dev.
const HUB_URL = import.meta.env.VITE_API_URL
  ? `${import.meta.env.VITE_API_URL}/hubs/etl`
  : '/hubs/etl'

let connection: HubConnection | null = null

/**
 * SignalR's `accessTokenFactory` contract is `Promise<string>`, so "no token" has to be the empty
 * string rather than null — the hub then falls back to the API-key header path below.
 */
async function getAccessToken(): Promise<string> {
  return (await getBearerToken()) ?? ''
}

export function getConnection(): HubConnection {
  if (connection) {
    return connection
  }

  const apiKey = getApiKey()
  const hasEntra = isEntraConfigured()

  const builder = new HubConnectionBuilder()
    .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
    .configureLogging(LogLevel.Warning)

  // withCredentials defaults to TRUE in @microsoft/signalr, so negotiate carried every localhost
  // cookie on top of the access token and came back 431 alongside the plain API calls. The hub
  // authenticates by token or X-API-Key like the rest of the surface — see services/api.ts fetchApi.
  if (hasEntra) {
    // Entra ID: use accessTokenFactory — SignalR sends token as query param for WebSocket
    builder.withUrl(HUB_URL, {
      accessTokenFactory: getAccessToken,
      withCredentials: false,
    })
  } else if (apiKey) {
    // API Key only: send as custom header on negotiate, use Long Polling + SSE
    // (WebSocket doesn't support custom headers, but negotiate is a regular HTTP POST)
    builder.withUrl(HUB_URL, {
      headers: { 'X-API-Key': apiKey },
      transport: HttpTransportType.ServerSentEvents | HttpTransportType.LongPolling,
      withCredentials: false,
    })
  } else {
    // No auth — connect anyway (server will reject if auth required)
    builder.withUrl(HUB_URL)
  }

  connection = builder.build()

  // Wire up state change notifications
  connection.onreconnecting(() => notifyStateListeners(false))
  connection.onreconnected(() => notifyStateListeners(true))
  connection.onclose(() => notifyStateListeners(false))

  return connection
}

export async function startConnection(): Promise<void> {
  const conn = getConnection()

  if (conn.state === HubConnectionState.Connected) {
    return
  }

  if (conn.state === HubConnectionState.Connecting || conn.state === HubConnectionState.Reconnecting) {
    return
  }

  try {
    await conn.start()
    notifyStateListeners(true)
  } catch (err) {
    console.warn('SignalR connection failed, will retry automatically:', err)
  }
}

export async function stopConnection(): Promise<void> {
  if (connection) {
    await connection.stop()
    connection = null
  }
}

export function isConnected(): boolean {
  return connection?.state === HubConnectionState.Connected
}

/** Subscribers notified when connection state changes. */
const stateListeners = new Set<(connected: boolean) => void>()

export function onConnectionStateChange(listener: (connected: boolean) => void): () => void {
  stateListeners.add(listener)
  return () => { stateListeners.delete(listener) }
}

function notifyStateListeners(connected: boolean) {
  for (const listener of stateListeners) {
    listener(connected)
  }
}
